import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast, TOAST_TYPE } from '../hooks/useToast';

const NOTIF_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'trades', label: 'Trades', types: ['TRADE_FILL'] },
  { id: 'faction', label: 'Faction', types: ['FACTION_ANNOUNCEMENT', 'WAR_DECLARED'] },
  { id: 'crimes', label: 'Crimes', types: ['CRIME_COMPLETE'] },
  { id: 'business', label: 'Business', types: ['BUSINESS_REVENUE'] },
];

const TYPE_ICON_MAP = {
  TRADE_FILL: { icon: 'TrendingUp', color: '#34D399' },
  FACTION_ANNOUNCEMENT: { icon: 'Users', color: '#60A5FA' },
  CRIME_COMPLETE: { icon: 'Skull', color: '#FB923C' },
  BUSINESS_REVENUE: { icon: 'Building2', color: '#FBBF24' },
  WAR_DECLARED: { icon: 'Swords', color: '#F87171' },
  LEVEL_UP: { icon: 'TrendingUp', color: '#A78BFA' },
  ACHIEVEMENT_UNLOCKED: { icon: 'Trophy', color: '#FBBF24' },
  PRICE_ALERT: { icon: 'Bell', color: '#FBBF24' },
  SYSTEM: { icon: 'Settings', color: '#94A3B8' },
};

const PREFS_KEY = 'empirex_notif_prefs';

const loadPrefs = () => {
  try {
    const s = localStorage.getItem(PREFS_KEY);
    return s ? JSON.parse(s) : { sound: false, dnd: false };
  } catch { return { sound: false, dnd: false }; }
};

const savePrefs = (prefs) => {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch {}
};

const relativeTime = (ts) => {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - new Date(ts)?.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(ts)?.toLocaleDateString();
};

// Notification Icon SVG
const NotifIcon = ({ type }) => {
  const cfg = TYPE_ICON_MAP?.[type] || TYPE_ICON_MAP?.SYSTEM;
  const icons = {
    TrendingUp: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={cfg?.color} strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
    Users: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={cfg?.color} strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
    Skull: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={cfg?.color} strokeWidth="2"><path d="M12 2a9 9 0 00-9 9c0 3.18 1.65 5.97 4.13 7.58L7 21h10l-.13-2.42A9 9 0 0021 11a9 9 0 00-9-9z"/><line x1="9" y1="15" x2="9.01" y2="15"/><line x1="15" y1="15" x2="15.01" y2="15"/></svg>,
    Building2: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={cfg?.color} strokeWidth="2"><path d="M6 22V4a2 2 0 012-2h8a2 2 0 012 2v18z"/><path d="M6 12H4a2 2 0 00-2 2v6a2 2 0 002 2h2"/><path d="M18 9h2a2 2 0 012 2v9a2 2 0 01-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></svg>,
    Swords: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={cfg?.color} strokeWidth="2"><polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5"/><line x1="13" y1="19" x2="19" y2="13"/><line x1="16" y1="16" x2="20" y2="20"/><line x1="19" y1="21" x2="21" y2="19"/><polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5"/><line x1="5" y1="14" x2="9" y2="18"/><line x1="7" y1="21" x2="9" y2="19"/></svg>,
    Trophy: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={cfg?.color} strokeWidth="2"><polyline points="8 21 12 17 16 21"/><line x1="12" y1="17" x2="12" y2="11"/><path d="M7 4H4a2 2 0 000 4c0 2.5 2 4.5 5 5"/><path d="M17 4h3a2 2 0 010 4c0 2.5-2 4.5-5 5"/><rect x="7" y="2" width="10" height="9" rx="1"/></svg>,
    Bell: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={cfg?.color} strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>,
    Settings: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={cfg?.color} strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>,
  };
  return <span className="flex-shrink-0">{icons?.[cfg?.icon] || icons?.Bell}</span>;
};

// ── NotificationCenter ────────────────────────────────────────
const NotificationCenter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState('all');
  const [prefs, setPrefs] = useState(loadPrefs);
  const [showPrefs, setShowPrefs] = useState(false);
  const dropdownRef = useRef(null);
  const batchTimerRef = useRef({});
  const batchCountRef = useRef({});

  const unreadCount = notifications?.filter(n => !n?.read_at)?.length;

  // Add this function before useEffect that uses it
  const getToastTypeForNotif = useCallback((type) => {
    const map = {
      TRADE_FILL: TOAST_TYPE?.TRADE_SUCCESS,
      FACTION_ANNOUNCEMENT: TOAST_TYPE?.INFO,
      CRIME_COMPLETE: TOAST_TYPE?.CRIME_SUCCESS,
      BUSINESS_REVENUE: TOAST_TYPE?.SUCCESS,
      WAR_DECLARED: TOAST_TYPE?.FACTION_WAR,
      LEVEL_UP: TOAST_TYPE?.SUCCESS,
      ACHIEVEMENT_UNLOCKED: TOAST_TYPE?.SUCCESS,
      PRICE_ALERT: TOAST_TYPE?.PRICE_ALERT,
      SYSTEM: TOAST_TYPE?.INFO,
    };
    return map?.[type] || TOAST_TYPE?.INFO;
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) {
        setOpen(false);
        setShowPrefs(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Load notifications
  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(20);
      if (!error && data) setNotifications(data);
    } catch (e) {
      console.error('[NotificationCenter] Load error:', e);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) loadNotifications();
  }, [user?.id, loadNotifications]);

  // Real-time subscription
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase?.channel(`notifications:${user?.id}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user?.id}`,
        },
        (payload) => {
          const notif = payload?.new;
          if (!notif) return;

          // Add to list
          setNotifications(prev => [notif, ...prev]?.slice(0, 20));

          // Batching: group similar notifications within 5 minutes
          const batchKey = `${notif?.notification_type}`;
          const now = Date.now();
          if (!batchCountRef?.current?.[batchKey]) batchCountRef.current[batchKey] = 0;
          batchCountRef.current[batchKey]++;

          if (batchTimerRef?.current?.[batchKey]) {
            clearTimeout(batchTimerRef?.current?.[batchKey]);
          }

          batchTimerRef.current[batchKey] = setTimeout(() => {
            const count = batchCountRef?.current?.[batchKey] || 1;
            batchCountRef.current[batchKey] = 0;

            // Don't show toast in DND mode
            if (prefs?.dnd) return;

            // Show batched or single toast
            const toastType = getToastTypeForNotif(notif?.notification_type);
            const message = count > 1
              ? `${count} new ${notif?.notification_type?.toLowerCase()?.replace(/_/g, ' ')} notifications`
              : notif?.message;
            const title = count > 1 ? `${count} Updates` : notif?.title;

            showToast(message, toastType, {
              title,
              actions: notif?.link_to ? [
                { label: 'View', primary: true, onClick: () => navigate(notif?.link_to) },
              ] : undefined,
            });

            // Sound
            if (prefs?.sound) {
              try {
                const ctx = new (window.AudioContext || window.webkitAudioContext)();
                const osc = ctx?.createOscillator();
                const gain = ctx?.createGain();
                osc?.connect(gain);
                gain?.connect(ctx?.destination);
                osc.frequency.value = 880;
                gain?.gain?.setValueAtTime(0.1, ctx?.currentTime);
                gain?.gain?.exponentialRampToValueAtTime(0.001, ctx?.currentTime + 0.3);
                osc?.start(ctx?.currentTime);
                osc?.stop(ctx?.currentTime + 0.3);
              } catch {}
            }
          }, 300); // 300ms batch window
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }, [user?.id, prefs?.dnd, prefs?.sound, showToast, navigate, getToastTypeForNotif]);

  const markRead = useCallback(async (id) => {
    setNotifications(prev => prev?.map(n => n?.id === id ? { ...n, read_at: new Date()?.toISOString() } : n));
    try {
      await supabase?.from('notifications')?.update({ read_at: new Date()?.toISOString() })?.eq('id', id);
    } catch {}
  }, []);

  const markAllRead = useCallback(async () => {
    const now = new Date()?.toISOString();
    setNotifications(prev => prev?.map(n => ({ ...n, read_at: n?.read_at || now })));
    try {
      if (user?.id) {
        await supabase?.rpc('mark_all_notifications_read', { p_user_id: user?.id });
      }
    } catch {}
  }, [user?.id]);

  const clearAll = useCallback(async () => {
    setNotifications([]);
    try {
      if (user?.id) {
        await supabase?.from('notifications')?.delete()?.eq('user_id', user?.id);
      }
    } catch {}
  }, [user?.id]);

  const handleNotifClick = (notif) => {
    markRead(notif?.id);
    if (notif?.link_to) navigate(notif?.link_to);
    setOpen(false);
  };

  const updatePrefs = (updates) => {
    const next = { ...prefs, ...updates };
    setPrefs(next);
    savePrefs(next);
  };

  // Filter notifications by category
  const filteredNotifs = notifications?.filter(n => {
    if (category === 'all') return true;
    const cat = NOTIF_CATEGORIES?.find(c => c?.id === category);
    return cat?.types?.includes(n?.notification_type);
  });

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => { setOpen(p => !p); if (!open) loadNotifications(); }}
        className="relative w-9 h-9 rounded-lg flex items-center justify-center transition-all hover:bg-white/5"
        style={{ border: open ? '1px solid rgba(255,255,255,0.15)' : '1px solid transparent' }}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted-foreground">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 01-3.46 0"/>
        </svg>
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center"
            style={{ background: 'var(--color-primary, #7C3AED)', color: 'white' }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        {prefs?.dnd && (
          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-orange-500 border border-background" title="Do Not Disturb" />
        )}
      </button>
      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 rounded-2xl border shadow-2xl overflow-hidden z-[9998]"
          style={{
            background: 'var(--color-card, #1a1a2e)',
            borderColor: 'var(--color-border, rgba(255,255,255,0.1))',
            maxHeight: '480px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0" style={{ borderColor: 'var(--color-border, rgba(255,255,255,0.1))' }}>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-foreground">Notifications</span>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'var(--color-primary, #7C3AED)', color: 'white' }}>
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-[10px] px-2 py-1 rounded-md hover:bg-white/5 transition-colors" style={{ color: 'var(--color-primary, #7C3AED)' }}>
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setShowPrefs(p => !p)}
                className="w-6 h-6 rounded flex items-center justify-center hover:bg-white/5 transition-colors"
                title="Notification preferences"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted-foreground">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Preferences panel */}
          {showPrefs && (
            <div className="px-4 py-3 border-b flex-shrink-0" style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'var(--color-border, rgba(255,255,255,0.1))' }}>
              <p className="text-xs font-semibold text-foreground mb-2">Preferences</p>
              <div className="space-y-2">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-xs text-muted-foreground">Sound notifications</span>
                  <button
                    onClick={() => updatePrefs({ sound: !prefs?.sound })}
                    className={`w-8 h-4 rounded-full transition-all relative ${prefs?.sound ? 'bg-primary' : 'bg-white/10'}`}
                    style={{ background: prefs?.sound ? 'var(--color-primary, #7C3AED)' : 'rgba(255,255,255,0.1)' }}
                  >
                    <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${prefs?.sound ? 'left-4' : 'left-0.5'}`} />
                  </button>
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-xs text-muted-foreground">Do Not Disturb</span>
                  <button
                    onClick={() => updatePrefs({ dnd: !prefs?.dnd })}
                    className="w-8 h-4 rounded-full transition-all relative"
                    style={{ background: prefs?.dnd ? '#F97316' : 'rgba(255,255,255,0.1)' }}
                  >
                    <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${prefs?.dnd ? 'left-4' : 'left-0.5'}`} />
                  </button>
                </label>
                <button
                  onClick={() => {
                    if ('Notification' in window) {
                      Notification.requestPermission();
                    }
                  }}
                  className="w-full text-left text-xs px-2 py-1.5 rounded-md hover:bg-white/5 transition-colors"
                  style={{ color: 'var(--color-primary, #7C3AED)' }}
                >
                  🔔 Enable Push Notifications
                </button>
              </div>
            </div>
          )}

          {/* Category filters */}
          <div className="flex gap-1 px-3 py-2 border-b flex-shrink-0 overflow-x-auto" style={{ borderColor: 'var(--color-border, rgba(255,255,255,0.1))' }}>
            {NOTIF_CATEGORIES?.map(cat => (
              <button
                key={cat?.id}
                onClick={() => setCategory(cat?.id)}
                className={`flex-shrink-0 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                  category === cat?.id
                    ? 'text-zinc-950' :'text-muted-foreground hover:text-foreground'
                }`}
                style={category === cat?.id ? { background: 'var(--color-primary, #7C3AED)' } : { background: 'rgba(255,255,255,0.05)' }}
              >
                {cat?.label}
              </button>
            ))}
          </div>

          {/* Notifications list */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin text-muted-foreground">
                  <path d="M21 12a9 9 0 11-6.219-8.56" strokeLinecap="round" />
                </svg>
              </div>
            ) : filteredNotifs?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted-foreground mb-2">
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 01-3.46 0"/>
                </svg>
                <p className="text-xs text-muted-foreground">No notifications</p>
              </div>
            ) : (
              <div className="divide-y" style={{ borderColor: 'var(--color-border, rgba(255,255,255,0.05))' }}>
                {filteredNotifs?.map(notif => (
                  <button
                    key={notif?.id}
                    onClick={() => handleNotifClick(notif)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-all hover:bg-white/3 ${
                      !notif?.read_at ? 'bg-primary/3' : ''
                    }`}
                  >
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      <NotifIcon type={notif?.notification_type} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-xs font-semibold truncate ${!notif?.read_at ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {notif?.title}
                        </p>
                        <span className="text-[10px] text-muted-foreground/60 flex-shrink-0">{relativeTime(notif?.created_at)}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{notif?.message}</p>
                    </div>
                    {!notif?.read_at && (
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: 'var(--color-primary, #7C3AED)' }} />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications?.length > 0 && (
            <div className="flex items-center justify-between px-4 py-2 border-t flex-shrink-0" style={{ borderColor: 'var(--color-border, rgba(255,255,255,0.1))' }}>
              <button
                onClick={() => { navigate('/notifications'); setOpen(false); }}
                className="text-[11px] transition-colors hover:opacity-80"
                style={{ color: 'var(--color-primary, #7C3AED)' }}
              >
                View all notifications
              </button>
              <button
                onClick={clearAll}
                className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
